/**
 * EntitlementGate
 * ---------------
 * Wraps a page (or any subtree) and only renders its children if the
 * current user meets the required entitlement level.
 *
 * If the requirement isn't met, an upgrade prompt is shown instead.
 * Admins always pass.
 *
 * Reads claims via `useEntitlement()`, which decodes the Cognito ID
 * token populated by the pre-token-generation Lambda trigger.
 */

import { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { LockOutlined, RocketLaunchRounded } from '@mui/icons-material';
import type { EntitlementStatus } from '@charlava-com/jamcam-svc';
import { useEntitlement } from './useEntitlement';

interface EntitlementGateProps {
  /** Minimum status required to view children. Defaults to 'free' (any signed-in user). */
  required: EntitlementStatus;
  /** Friendly name used in the lock prompt. */
  featureName?: string;
  children: ReactNode;
  /** Optional override for the locked-state UI. */
  fallback?: ReactNode;
}

const TIER_LABEL: Record<EntitlementStatus, string> = {
  blocked: 'Blocked',
  free: 'Free',
  grace: 'Grace period',
  trial: 'Trial',
  paid: 'Full unlock',
};

export function EntitlementGate({
  required,
  featureName,
  children,
  fallback,
}: EntitlementGateProps) {
  const ent = useEntitlement();

  // Defer any decision until claims have been resolved.
  if (!ent.ready) return null;

  if (ent.can(required)) {
    return <>{children}</>;
  }

  if (fallback) return <>{fallback}</>;

  return (
    <Box sx={{ py: 6, display: 'grid', placeItems: 'center' }}>
      <Card
        variant="outlined"
        sx={{
          maxWidth: 460,
          width: '100%',
          backgroundImage: 'linear-gradient(150deg, #1a1f2e, #0e1018)',
          border: '1px solid rgba(139, 92, 246, 0.35)',
        }}
      >
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <LockOutlined color="secondary" />
              <Chip
                size="small"
                color="secondary"
                label={`${TIER_LABEL[required]} feature`}
                sx={{ fontWeight: 700 }}
              />
            </Stack>

            <Typography variant="h5">
              {featureName ?? 'This module'} is locked
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {ent.isAuthenticated
                ? `Your current plan is ${
                    TIER_LABEL[ent.entitlementStatus]
                  }. Upgrade to unlock ${
                    featureName ?? 'this module'
                  } and the full practice library for $29 once.`
                : `Sign in to unlock ${
                    featureName ?? 'this module'
                  }.`}
            </Typography>

            <Stack direction="row" spacing={1.5}>
              <Button
                component={RouterLink}
                to="/account"
                variant="contained"
                color="secondary"
                startIcon={<RocketLaunchRounded />}
              >
                View one-time unlock
              </Button>
              <Button
                component={RouterLink}
                to="/dashboard"
                variant="outlined"
                color="inherit"
              >
                Back to dashboard
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

export default EntitlementGate;
